import { useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ShieldCheck, Clock, Truck, Info } from "lucide-react";
import Layout from "./Layout";
import SEOHead from "./SEOHead";
import Breadcrumb from "./Breadcrumb";
import FAQSection from "./FAQSection";

// Indicative per-sq-ft rate ranges (INR), based on general industry market data.
// NOT a guaranteed price — actual quotes depend on structural design, site
// conditions and material specifications.
const BASE_RATES = {
  storage: { min: 900, max: 1400 },
  warehouse: { min: 1200, max: 2000 },
  manufacturing: { min: 1800, max: 2800 },
  factory: { min: 1800, max: 3200 },
};

const ADDONS = {
  insulatedRoofing: 150,
  heavyFlooring: 120,
  crane: 300,
};

const faqs = [
  { q: "Is this calculator giving me a final price?", a: "No — this is an indicative estimate based on general market rate ranges for the building type and options you select. Final quotation depends on structural design, site soil conditions, exact material specifications and current material costs." },
  { q: "Why does the estimate show a range instead of one number?", a: "PEB pricing genuinely varies with span, exact load requirements and site conditions even within the same building type — a range reflects that honestly rather than implying false precision." },
  { q: "What's not included in this estimate?", a: "This estimate covers the structure only. Foundation/civil work, MEP (electrical, plumbing, fire safety), statutory approvals and site development are typically separate and should be discussed in a detailed project estimate." },
];

const PEBCostCalculator = () => {
  const [buildingType, setBuildingType] = useState("warehouse");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [insulatedRoofing, setInsulatedRoofing] = useState(false);
  const [heavyFlooring, setHeavyFlooring] = useState(false);
  const [crane, setCrane] = useState(false);
  const [result, setResult] = useState(null);

  const handleCalculate = (e) => {
    e.preventDefault();
    const l = parseFloat(length);
    const w = parseFloat(width);
    if (!l || !w || l <= 0 || w <= 0) return;

    const area = l * w;
    const base = BASE_RATES[buildingType];
    let addonMin = 0;
    let addonMax = 0;
    if (insulatedRoofing) { addonMin += ADDONS.insulatedRoofing; addonMax += ADDONS.insulatedRoofing * 1.3; }
    if (heavyFlooring) { addonMin += ADDONS.heavyFlooring; addonMax += ADDONS.heavyFlooring * 1.3; }
    if (crane) { addonMin += ADDONS.crane; addonMax += ADDONS.crane * 1.4; }

    const rateMin = base.min + addonMin;
    const rateMax = base.max + addonMax;

    setResult({
      area,
      rateMin,
      rateMax,
      totalMin: Math.round((area * rateMin) / 100000) / 10,
      totalMax: Math.round((area * rateMax) / 100000) / 10,
    });
  };

  return (
    <Layout>
      <SEOHead
        title="PEB Cost Calculator | Vasavi Structures"
        description="Get an indicative PEB structure cost estimate based on building type, dimensions and specifications from Vasavi Structures' engineering team in Hyderabad."
        keywords="PEB cost calculator, industrial shed cost calculator, PEB price estimate Hyderabad"
      />
      <Breadcrumb items={[{ name: "PEB Cost Calculator" }]} />

      {/* 1. Hero */}
      <section className="section-padding pb-8">
        <div className="container-custom text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Calculator className="text-primary" size={28} aria-hidden="true" />
          </div>
          <h1 className="font-montserrat text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            PEB Cost <span className="text-secondary">Calculator</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Get an indicative estimate for your PEB structure based on building type, size and
            specifications. This is a starting point for budgeting — not a final quotation.
          </p>
        </div>
      </section>

      {/* 2. Trust strip */}
      <section className="bg-primary py-6">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
            <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Market-range based estimate</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> Instant indicative result</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Truck size={18} aria-hidden="true" /> Follow-up with a real quote</span>
          </div>
        </div>
      </section>

      {/* 3. Calculator */}
      <section className="section-padding">
        <div className="container-custom max-w-2xl">
          <form onSubmit={handleCalculate} className="space-y-6 bg-card p-6 md:p-8 rounded-2xl shadow-lg border border-border">
            <div>
              <label className="block font-medium text-foreground mb-2">Building Type</label>
              <select
                value={buildingType}
                onChange={(e) => setBuildingType(e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
              >
                <option value="storage">Storage Shed</option>
                <option value="warehouse">Warehouse</option>
                <option value="manufacturing">Manufacturing Shed</option>
                <option value="factory">Factory Building</option>
              </select>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-medium text-foreground mb-2">Length (ft)</label>
                <input
                  type="number"
                  min="1"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  placeholder="e.g. 200"
                  required
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block font-medium text-foreground mb-2">Width (ft)</label>
                <input
                  type="number"
                  min="1"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="e.g. 100"
                  required
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block font-medium text-foreground mb-3">Additional Specifications</label>
              <div className="space-y-2">
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={insulatedRoofing} onChange={(e) => setInsulatedRoofing(e.target.checked)} className="w-4 h-4" />
                  <span className="text-muted-foreground">Insulated roofing (PUF panels)</span>
                </label>
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={heavyFlooring} onChange={(e) => setHeavyFlooring(e.target.checked)} className="w-4 h-4" />
                  <span className="text-muted-foreground">Heavy-duty (VDF) flooring</span>
                </label>
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={crane} onChange={(e) => setCrane(e.target.checked)} className="w-4 h-4" />
                  <span className="text-muted-foreground">EOT crane provision</span>
                </label>
              </div>
            </div>

            <button type="submit" className="btn-secondary w-full py-4 text-lg font-medium">
              Calculate Indicative Estimate
            </button>
          </form>

          {result && (
            <div className="mt-8 p-6 md:p-8 bg-primary/5 border-2 border-primary rounded-2xl">
              <p className="text-sm text-muted-foreground mb-2">Built-up area: {result.area.toLocaleString()} sq ft</p>
              <p className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-2">
                ₹{result.totalMin} – ₹{result.totalMax} Crores
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Indicative range at ₹{result.rateMin}–₹{result.rateMax} per sq ft for the structure.
              </p>
              <div className="flex items-start gap-2 p-4 bg-secondary/10 rounded-lg mb-6">
                <Info size={18} className="text-secondary flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-muted-foreground">
                  Indicative estimate only. Final quotation depends on structural design, material
                  specifications, site conditions and project requirements.
                </p>
              </div>
              <Link to="/enquiry" className="btn-primary block text-center py-4">
                Get a Detailed Project Estimate
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* 4. How we calculate */}
      <section className="section-padding bg-muted">
        <div className="container-custom max-w-3xl">
          <h2 className="font-montserrat text-2xl font-bold text-foreground mb-4 text-center">
            How This Estimate Is Calculated
          </h2>
          <p className="text-muted-foreground text-center">
            The estimate uses general market rate ranges per sq ft for each building type, adjusted for
            the specifications you select (insulated roofing, heavy-duty flooring, crane provision).
            It's meant for early-stage budgeting — an accurate quote requires a full structural design
            based on your specific span, height, load requirements and site conditions.
          </p>
        </div>
      </section>

      {/* 5. FAQ */}
      <FAQSection faqs={faqs} />

      {/* 6. CTA */}
      <section className="section-padding bg-secondary">
        <div className="container-custom text-center">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
            Want an Accurate, Project-Specific Quote?
          </h2>
          <p className="text-secondary-foreground/90 mb-8 max-w-xl mx-auto">
            Share your project details and our engineering team will get back with a real estimate.
          </p>
          <Link to="/enquiry" className="btn-primary inline-block px-8 py-4">
            Get Detailed Estimate
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default PEBCostCalculator;
